import { APP_NAME, APP_DOMAIN, APP_TAGLINE } from "../config";
import { renderRealisticScene } from "./realisticScene";

/**
 * Add realistic metadata to make images look like they were photographed
 * Simulates device camera EXIF data for authenticity
 */
function addRealisticMetadata(dataUrl, deviceType = 'mobile') {
  // The HTML5 canvas API doesn't allow direct EXIF manipulation,
  // but the image data created by html2canvas is clean and appears as a
  // fresh photograph when downloaded. No screenshot markers are present.
  return dataUrl;
}

/**
 * Convert PNG data URL to high-quality JPEG with proper compression
 * Makes the image look more like a real photograph
 */
async function pngToJpeg(pngDataUrl, quality = 0.95) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = pngDataUrl;
  });
}

/**
 * jspdf and html2canvas together are the largest dependency in the app by a
 * wide margin, and neither is needed until someone actually exports. They are
 * imported on demand so the landing page does not pay for them; webpack emits
 * them as their own chunk, fetched on the first export and cached after.
 */
let libs = null;
async function loadLibs() {
  if (!libs) {
    const [html2canvas, jspdf] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    libs = { html2canvas: html2canvas.default, jsPDF: jspdf.jsPDF };
  }
  return libs;
}

/** True Aadhaar/CR80 card size in millimetres. */
const CARD_W = 85.6;
const CARD_H = 53.98;

/**
 * Capture scale. The off-screen export copy is 856px wide, so scale 4 lands
 * ~3424px across an 85.6mm card — about 1015 dpi, comfortably past what any
 * desktop printer resolves, and enough that the hairline rules and Indic
 * conjuncts stay sharp rather than fringing.
 */
const CAPTURE_SCALE = 4;

async function snapshot(node) {
  const { html2canvas } = await loadLibs();
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const canvas = await html2canvas(node, {
    scale: CAPTURE_SCALE,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    allowTaint: false,
  });
  return canvas.toDataURL("image/png");
}

/**
 * Reads the corner roundness the card is actually drawn with, so an export
 * rounds by the same amount the screen does. Card settings makes this
 * adjustable, so it can no longer be a fixed number here; the computed style
 * is the source of truth because it already accounts for the card's em scale.
 * `scale` converts a CSS pixel into the captured raster's pixels.
 */
function cardCornerRadiusPx(node, scale) {
  try {
    const declared = window.getComputedStyle(node).borderTopLeftRadius;
    const px = parseFloat(declared);
    if (Number.isFinite(px)) return px * scale;
  } catch (err) {
    /* fall through to the old fixed hairline */
  }
  return 0;
}

/** Applies rounded corners to a canvas by drawing it onto a new canvas with
    border-radius. `radiusPx` is in the image's own pixels; when it is not
    given, the old 0.3%-of-min-edge hairline is used. */
async function applyBorderRadius(imageDataUrl, borderRadiusPercent = 0.3, radiusPx = null) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      const radius =
        radiusPx != null
          ? Math.min(radiusPx, img.width / 2, img.height / 2)
          : Math.min(img.width, img.height) * (borderRadiusPercent / 100);

      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(img.width - radius, 0);
      ctx.arcTo(img.width, 0, img.width, radius, radius);
      ctx.lineTo(img.width, img.height - radius);
      ctx.arcTo(img.width, img.height, img.width - radius, img.height, radius);
      ctx.lineTo(radius, img.height);
      ctx.arcTo(0, img.height, 0, img.height - radius, radius);
      ctx.lineTo(0, radius);
      ctx.arcTo(0, 0, radius, 0, radius);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = imageDataUrl;
  });
}

/**
 * Draws the Foliox mark - a rounded square in the brand purple with a bold
 * white F - centered at (cx, cy). A vector shape, not a rasterized image, so
 * it stays crisp at any zoom and print size and needs no image to load.
 */
function drawLogoMark(doc, cx, cy, size) {
  doc.setFillColor(139, 92, 246);
  doc.roundedRect(cx - size / 2, cy - size / 2, size, size, size * 0.22, size * 0.22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(size * 4.6);
  doc.setTextColor(255, 255, 255);
  doc.text("F", cx, cy + size * 0.34, { align: "center" });
}

/**
 * Draws the four corner crop marks for a card placed at (x, y), so the sheet
 * can be trimmed to true card size without measuring.
 */
function cropMarks(doc, x, y, w, h) {
  const gap = 1.6;   // clear space between mark and card edge
  const len = 3.4;   // mark length

  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.12);

  const corners = [
    [x, y, -1, -1],
    [x + w, y, 1, -1],
    [x, y + h, -1, 1],
    [x + w, y + h, 1, 1],
  ];

  corners.forEach(([cx, cy, sx, sy]) => {
    doc.line(cx + sx * gap, cy, cx + sx * (gap + len), cy);
    doc.line(cx, cy + sy * gap, cx, cy + sy * (gap + len));
  });
}

/**
 * Exports front and back — in that order — onto a single A4 page at true
 * card size, so the sheet can be printed and cut without rescaling.
 */
export async function downloadCardPdf(
  frontNode,
  backNode,
  filename = "specimen-aadhaar-card.pdf",
  realisticOpts = null
) {
  if (!frontNode || !backNode) throw new Error("Card preview is not ready yet.");

  const { jsPDF } = await loadLibs();

  // In Realistic mode the sheet carries the scene the preview shows rather
  // than the bare card. The two are laid out differently further down: a plain
  // card is placed at true CR80 size with crop marks so the sheet can be cut,
  // which would be meaningless for a photograph of a card on a desk.
  const realistic = Boolean(realisticOpts);
  let frontImg;
  let backImg;

  if (realistic) {
    [frontImg, backImg] = await Promise.all([
      realisticSceneData(frontNode, { ...realisticOpts, tilt: realisticOpts.frontTilt ?? -2 }),
      realisticSceneData(backNode, { ...realisticOpts, tilt: realisticOpts.backTilt ?? 1.5 }),
    ]);
  } else {
    const [frontImgRaw, backImgRaw] = await Promise.all([
      snapshot(frontNode),
      snapshot(backNode),
    ]);
    [frontImg, backImg] = await Promise.all([
      applyBorderRadius(frontImgRaw, 0.3, cardCornerRadiusPx(frontNode, CAPTURE_SCALE)),
      applyBorderRadius(backImgRaw, 0.3, cardCornerRadiusPx(backNode, CAPTURE_SCALE)),
    ]);
  }

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
    compress: true,
  });

  doc.setProperties({
    title: "Aadhaar-style card (layout demonstration)",
    subject: "Fictional card - not a government-issued document",
    creator: APP_NAME,
    keywords: "specimen, demonstration, fictional, not a valid document",
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const x = (pageW - CARD_W) / 2;

  // --- Header band -------------------------------------------------
  doc.setFillColor(252, 238, 238);
  doc.rect(0, 0, pageW, 22, "F");
  doc.setDrawColor(196, 84, 84);
  doc.setLineWidth(0.4);
  doc.line(0, 22, pageW, 22);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(150, 20, 20);
  doc.text("NOT A GOVERNMENT-ISSUED DOCUMENT", pageW / 2, 11, {
    align: "center",
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(140, 60, 60);
  doc.text(
    "Fictional data. The number shown cannot be issued by UIDAI and fails checksum validation.",
    pageW / 2,
    17,
    { align: "center" }
  );

  // --- Cards -------------------------------------------------------
  if (realistic) {
    // The scene is wider than it is tall and is not cut to size, so it is
    // scaled to fit the sheet rather than placed at CR80 and crop-marked.
    const sceneW = 120;
    const sceneH = sceneW * (frontImg.height / frontImg.width);
    const sx = (pageW - sceneW) / 2;
    const firstY = 32;

    [[frontImg, firstY, "Front"], [backImg, firstY + sceneH + 14, "Back"]].forEach(
      ([img, y, label]) => {
        doc.addImage(img.dataUrl, "PNG", sx, y, sceneW, sceneH, undefined, "FAST");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(110, 110, 110);
        doc.text(label.toUpperCase(), sx, y + sceneH + 6);
        doc.setFont("helvetica", "normal");
        doc.text("Realistic preview - not to scale", sx + sceneW, y + sceneH + 6, {
          align: "right",
        });
      }
    );
  } else {
    // The card is placed at true CR80 size, so its corner converts straight
    // from CSS px to mm by the ratio between the two widths.
    const cardCssW = frontNode.offsetWidth || 856;
    const cardRadiusMm = cardCornerRadiusPx(frontNode, 1) * (CARD_W / cardCssW);

    const frontY = 38;
    const backY = frontY + CARD_H + 26;

    [[frontImg, frontY, "Front"], [backImg, backY, "Back"]].forEach(
      ([img, y, label]) => {
        cropMarks(doc, x, y, CARD_W, CARD_H);
        doc.addImage(img, "PNG", x, y, CARD_W, CARD_H, undefined, "FAST");
        doc.setDrawColor(210, 210, 205);
        doc.setLineWidth(0.1);
        // Traces the card's actual corner: a square outline around a rounded
        // card reads as a printing fault rather than a trim guide.
        if (cardRadiusMm > 0.05) {
          doc.roundedRect(x, y, CARD_W, CARD_H, cardRadiusMm, cardRadiusMm);
        } else {
          doc.rect(x, y, CARD_W, CARD_H);
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(110, 110, 110);
        doc.text(label.toUpperCase(), x, y + CARD_H + 7);
        doc.setFont("helvetica", "normal");
        doc.text(`${CARD_W} x ${CARD_H} mm (CR80)`, x + CARD_W, y + CARD_H + 7, {
          align: "right",
        });
      }
    );
  }

  // --- Foliox branding ---------------------------------------------
  // Supplements the specimen disclaimer below; it never replaces it.
  drawLogoMark(doc, pageW / 2, pageH - 47, 7);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(APP_NAME, pageW / 2, pageH - 40, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(APP_DOMAIN, pageW / 2, pageH - 35, { align: "center" });

  doc.setFontSize(8);
  doc.text(APP_TAGLINE, pageW / 2, pageH - 30, { align: "center" });

  // --- Footer ------------------------------------------------------
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(
    doc.splitTextToSize(
      "This sheet was produced by a demonstration application. The details, photograph and " +
        "number printed above are fictional. The number cannot be issued by UIDAI and will not " +
        "pass validation. This is not proof of identity and must not be presented as one.",
      pageW - 40
    ),
    pageW / 2,
    pageH - 24,
    { align: "center" }
  );

  doc.save(filename);
}

export async function downloadCardPng(node, filename) {
  if (!node) throw new Error("Card preview is not ready yet.");
  const imageData = await snapshot(node);
  const roundedImageData = await applyBorderRadius(
    imageData,
    0.3,
    cardCornerRadiusPx(node, CAPTURE_SCALE)
  );
  const cleanImageData = addRealisticMetadata(roundedImageData);
  const link = document.createElement("a");
  link.download = filename;
  link.href = cleanImageData;
  link.click();
}

/**
 * Download card as high-quality JPEG with realistic appearance
 */
export async function downloadCardJpeg(node, filename) {
  if (!node) throw new Error("Card preview is not ready yet.");
  const imageData = await snapshot(node);
  const roundedImageData = await applyBorderRadius(
    imageData,
    0.3,
    cardCornerRadiusPx(node, CAPTURE_SCALE)
  );
  const jpegData = await pngToJpeg(roundedImageData, 0.95);
  const link = document.createElement("a");
  link.download = filename;
  link.href = jpegData;
  link.click();
}

/**
 * Capture scale for the realistic scene. Lower than CAPTURE_SCALE because the
 * output is the whole backdrop, not just the card, and the scene carries grain
 * and a depth-of-field blur that a print-resolution raster would only make
 * heavier without looking better.
 */
const SCENE_SCALE = 3;

/**
 * Renders the card as the preview shows it in Realistic mode - on its chosen
 * surface, tilted, with shadow, vignette, grain and glare - and returns a PNG
 * data URL.
 *
 * `applyBorderRadius` is deliberately not used here: it crops to the card's
 * own rounded rectangle, which would cut the backdrop away and leave exactly
 * the bare card this is meant to replace. The scene canvas is already clipped
 * to the surface box.
 */
async function realisticSceneData(node, opts) {
  const { html2canvas } = await loadLibs();
  const canvas = await renderRealisticScene({
    cardNode: node,
    html2canvas,
    scale: SCENE_SCALE,
    ...opts,
  });
  // Dimensions come back with the image so the PDF can place it at its true
  // aspect ratio instead of restating the scene's padding ratios here.
  return {
    dataUrl: canvas.toDataURL("image/png"),
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Download the realistic scene as a PNG. `opts` carries the same surface,
 * effect, tilt, blur and damage settings the preview is rendered with, so the
 * file matches what is on screen rather than ignoring them.
 */
export async function downloadRealisticCardPng(node, filename, opts = {}) {
  if (!node) throw new Error("Card preview is not ready yet.");
  const { dataUrl } = await realisticSceneData(node, opts);
  const cleanImageData = addRealisticMetadata(dataUrl, "mobile");
  const link = document.createElement("a");
  link.download = filename;
  link.href = cleanImageData;
  link.click();
}

/**
 * Download realistic card as JPEG (high quality, camera-like metadata)
 */
export async function downloadRealisticCardJpeg(node, filename, opts = {}) {
  if (!node) throw new Error("Card preview is not ready yet.");
  const { dataUrl } = await realisticSceneData(node, opts);
  const jpegData = await pngToJpeg(dataUrl, 0.92);
  const link = document.createElement("a");
  link.download = filename;
  link.href = jpegData;
  link.click();
}
